import { useEffect, useState } from "react";
import { API_BASE_URL, APP_SCHEME, ANDROID_PACKAGE } from "@/constants";
import { getMobilePlatform } from "@/utils/webview";

// Animates the live APY from 1% up to the current value on mount.
export function useAPY() {
  const [displayed, setDisplayed] = useState(1);

  useEffect(() => {
    fetch(`${API_BASE_URL}/v3/metrics/current_apy`)
      .then((r) => r.json())
      .then((data) => {
        const target = typeof (data as { current_apy?: unknown }).current_apy === "number"
          ? (data as { current_apy: number }).current_apy * 100
          : 5;
        animate(target);
      })
      .catch(() => {
        animate(5);
      });

    function animate(target: number) {
      const start = performance.now();
      let raf: number;
      const tick = (now: number) => {
        const t = Math.min((now - start) / 2000, 1);
        setDisplayed(1 + (target - 1) * (1 - Math.pow(1 - t, 3)));
        if (t < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }
  }, []);

  return `${displayed.toFixed(1)}%`;
}

export async function writeToClipboard(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0;";
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(ta);
  if (!copied) throw new Error("Clipboard copy was rejected by the browser");
}

// Copies the invite code and hands off to the native app (or the store).
// Must be called synchronously from a tap handler — awaiting anything first
// breaks user activation and the browser blocks the app-open navigation.
export function claimInviteAndOpenApp(
  inviteCode: string,
  onReturnToBrowser?: () => void,
) {
  writeToClipboard(inviteCode).catch(() => {});

  const platform = getMobilePlatform();

  // Android: an intent:// URL lets Chrome natively open the app if installed.
  // If not, browser_fallback_url sends them to the same /app/auth/invite-code
  // interstitial as iOS, which then redirects to the Play Store — so both
  // platforms get the identical graceful "Getting Goldsand…" step.
  if (platform === "android") {
    const fallback = encodeURIComponent(`${window.location.origin}/app/auth/invite-code`);
    window.location.href =
      `intent://auth/invite-code#Intent;scheme=${APP_SCHEME};package=${ANDROID_PACKAGE};S.browser_fallback_url=${fallback};end`;
    return;
  }

  // iOS: navigate to the universal link. If the app is installed, iOS opens it;
  // otherwise the browser loads /app/auth/invite-code, which redirects to the
  // App Store. Using an https URL avoids Safari's "address invalid" dialog that
  // an unhandled custom scheme would throw when the app isn't installed.
  if (platform === "ios") {
    // If the app opens, this tab is backgrounded — let the caller reset its UI
    // so the page looks normal if the user later switches back to Safari.
    if (onReturnToBrowser) {
      const onVisibilityChange = () => {
        if (!document.hidden) return;
        onReturnToBrowser();
        document.removeEventListener("visibilitychange", onVisibilityChange);
      };
      document.addEventListener("visibilitychange", onVisibilityChange);
    }
    window.location.href = `${window.location.origin}/app/auth/invite-code`;
    return;
  }

  // Desktop / unknown: no app to open — go straight to the download page.
  window.location.href = "/download";
}
